import { getDataWithRetry } from '../fetchUtils';
import { apiBaseUrl, DEFAULT_FROM_DATE } from '../apiConsts';

import type { SessionStatistic } from '../../types';
import type { FetchResult, FetchSuccess } from '../fetchUtils';
import { isRecord, isSessionStatistic } from '../../utils/typeUtils';

type SessionStatisticsRangeResponse = Record<string, SessionStatistic[]>;
type SessionStatisticsLastResponse = Record<string, SessionStatistic>;

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

const isSessionStatisticsRangeResponse = (
  data: unknown,
): data is SessionStatisticsRangeResponse => {
  if (!isRecord(data)) return false;
  return Object.values(data).every(
    (value) =>
      Array.isArray(value) && value.every((stat) => isSessionStatistic(stat)),
  );
};

const isSessionStatisticsLastResponse = (
  data: unknown,
): data is SessionStatisticsLastResponse => {
  if (!isRecord(data)) return false;
  return Object.values(data).every((value) => isSessionStatistic(value));
};

export const fetchSessionStatistics = async (
  memberIds: string[],
  from: string,
  to: string,
  useDefaultFromDate?: boolean,
): Promise<FetchResult<Record<string, SessionStatistic[]>>> => {
  if (!memberIds?.length) {
    return createSuccessResult<Record<string, SessionStatistic[]>>({});
  }
  const effectiveFrom =
    useDefaultFromDate && new Date(from) < new Date(DEFAULT_FROM_DATE)
      ? DEFAULT_FROM_DATE
      : from;

  const response = await getDataWithRetry<SessionStatisticsRangeResponse>(
    `${apiBaseUrl}session-statistics/range`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ memberIds, from: effectiveFrom, to }),
    },
    isSessionStatisticsRangeResponse,
  );
  return response;
};

export const fetchLastSessionStatistics = async (
  memberIds: string[],
): Promise<FetchResult<Record<string, SessionStatistic>>> => {
  if (!memberIds?.length) {
    return createSuccessResult<Record<string, SessionStatistic>>({});
  }
  const response = await getDataWithRetry<SessionStatisticsLastResponse>(
    `${apiBaseUrl}session-statistics/last/getByIds`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([...memberIds]),
    },
    isSessionStatisticsLastResponse,
  );
  return response;
};
